import { useCallback, useEffect, useMemo, useState } from 'react';

import { ThemeContext, THEME_STORAGE_KEY } from './themeContextObject.js';

/**
 * ThemeProvider — light/dark theme state.
 *
 * Light is the default. The OS `prefers-color-scheme` setting is deliberately
 * *not* used to pick the initial theme; the product decision is that the site
 * opens light unless the visitor has explicitly chosen otherwise.
 *
 * The initial value is read from the DOM rather than from storage, because
 * public/theme-init.js has already applied the class before this component
 * mounts. Reading the DOM keeps a single source of truth and guarantees the React
 * state cannot disagree with what is on screen.
 */

function readInitialTheme() {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(readInitialTheme);

  // Reflect the theme onto <html> and persist the choice.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* Storage can be unavailable in private modes; the theme still applies. */
    }
  }, [theme]);

  // Keep multiple open tabs in sync. The `storage` event only fires in *other*
  // tabs, so this cannot loop back on the tab that made the change.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === THEME_STORAGE_KEY && (event.newValue === 'dark' || event.newValue === 'light')) {
        setTheme(event.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo(
    () => ({ theme, isDark: theme === 'dark', setTheme, toggleTheme }),
    [theme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
